import { z } from "zod";

/**
 * Action type enum matching the internal Zapier API
 */
export const ActionTypeSchema = z.enum([
  "filter",
  "read",
  "read_bulk",
  "run",
  "search",
  "search_and_write",
  "search_or_write",
  "write",
]);

export type ActionType = z.infer<typeof ActionTypeSchema>;

/**
 * Base Action schema matching the internal API response from /api/v4/implementations/
 */
export const ActionSchema = z.object({
  id: z.string().optional().describe("Unique identifier for the action"),
  type: ActionTypeSchema.describe("The type of action"),
  key: z.string().describe("Unique key identifier for the action"),
  name: z.string().describe("Display name of the action"),
  description: z.string().describe("Description of what the action does"),
  is_important: z
    .boolean()
    .optional()
    .describe("Whether this action is marked as important"),
  is_hidden: z
    .boolean()
    .optional()
    .describe("Whether this action is hidden from listings"),
  selected_api: z
    .string()
    .optional()
    .describe("The implementation ID this action belongs to"),
});

export type Action = z.infer<typeof ActionSchema>;

/**
 * Normalized action item returned by the list actions endpoint
 *
 * Transforms API response fields:
 * - name → title
 * - type → action_type
 * - selected_api → app_key + app_version (parsed)
 *
 * Adds computed fields:
 * - type: "action" (literal type identifier)
 * - app_key: Extracted from selected_api (e.g., "SlackCLIAPI@1.0.0" → "SlackCLIAPI")
 * - app_version: Extracted from selected_api (e.g., "SlackCLIAPI@1.0.0" → "1.0.0")
 */
export const ActionItemSchema = z
  .object({
    id: z.string().optional().describe("Unique identifier for the action"),
    key: z.string().describe("Unique key identifier for the action"),
    description: z.string().describe("Description of what the action does"),
    is_important: z
      .boolean()
      .optional()
      .describe("Whether this action is marked as important"),
    is_hidden: z
      .boolean()
      .optional()
      .describe("Whether this action is hidden from listings"),
    app_key: z
      .string()
      .describe("App key extracted from implementation ID (without version)"),
    app_version: z
      .string()
      .optional()
      .describe("App version extracted from implementation ID"),
    action_type: ActionTypeSchema.describe("The type of action"),
    title: z.string().describe("Display name of the action (mapped from name)"),
    type: z.literal("action").describe("Type identifier for this item"),
  })
  .describe("Normalized action item returned by list actions endpoint");

export type ActionItem = z.infer<typeof ActionItemSchema>;

/**
 * Implementation schema with actions array for the /api/v4/implementations/ response
 */
export const ImplementationWithActionsSchema = z.object({
  selected_api: z.string().describe("The implementation ID"),
  app_id: z.number().optional().describe("Numeric app ID"),
  auth_type: z.string().optional().describe("Authentication type"),
  actions: z
    .array(ActionSchema)
    .optional()
    .describe("Array of actions for this implementation"),
  is_deprecated: z
    .boolean()
    .optional()
    .describe("Whether the implementation is deprecated"),
  is_private_only: z
    .boolean()
    .optional()
    .describe("Whether the implementation is private only"),
  is_invite_only: z
    .boolean()
    .optional()
    .describe("Whether the implementation is invite only"),
  is_beta: z
    .boolean()
    .optional()
    .default(false)
    .describe("Whether the implementation is in beta"),
  is_premium: z
    .boolean()
    .optional()
    .default(false)
    .describe("Whether the implementation requires premium"),
  name: z.string().optional().describe("Name of the implementation"),
  slug: z.string().optional().describe("URL-friendly slug"),
});

export type ImplementationWithActions = z.infer<
  typeof ImplementationWithActionsSchema
>;

/**
 * Response schema for the /api/v4/implementations/ endpoint
 */
export const ImplementationsWithActionsResponseSchema = z.object({
  count: z.number().describe("Total number of results"),
  next: z
    .string()
    .nullable()
    .optional()
    .describe("URL for the next page of results"),
  previous: z
    .string()
    .nullable()
    .optional()
    .describe("URL for the previous page of results"),
  results: z
    .array(ImplementationWithActionsSchema)
    .describe("Array of implementations with their actions"),
});

export type ImplementationsWithActionsResponse = z.infer<
  typeof ImplementationsWithActionsResponseSchema
>;

/**
 * Query parameters for listing actions
 */
export const ListActionsQuerySchema = z
  .object({
    /** @deprecated Use app_key instead */
    appKey: z
      .string()
      .optional()
      .describe(
        "Deprecated: Use app_key instead. App key to list actions for (e.g., 'SlackCLIAPI@1.21.1' or 'SlackCLIAPI')",
      ),
    app_key: z
      .string()
      .optional()
      .describe(
        "App key to list actions for (e.g., 'SlackCLIAPI@1.21.1' or 'SlackCLIAPI')",
      ),
    /** @deprecated Use action_type instead */
    actionType: ActionTypeSchema.optional().describe(
      "Deprecated: Use action_type instead. Filter actions by type (e.g., 'read', 'write', 'search')",
    ),
    action_type: ActionTypeSchema.optional().describe(
      "Filter actions by type (e.g., 'read', 'write', 'search')",
    ),
  })
  .refine((data) => data.appKey || data.app_key, {
    message: "Either app_key or appKey is required",
    path: ["app_key"],
  })
  .describe("Query parameters for listing actions");

/**
 * Response schema for listActions
 */
export const ListActionsResponseSchema = z
  .object({
    data: z.array(ActionItemSchema).describe("Array of action items"),
  })
  .describe("Response schema for listActions");

/**
 * TypeScript types for request and response
 */
export type ListActionsQuery = z.infer<typeof ListActionsQuerySchema>;
export type ListActionsResponse = z.infer<typeof ListActionsResponseSchema>;
